/**
 * TipTapEditor — Rich text editor component
 * Drop-in replacement for ReactQuill in EditFormV1
 *
 * Props:
 *   value      string    — HTML string (controlled)
 *   onChange   function  — called with HTML string on every change
 *   disabled   boolean   — makes editor read-only
 *   placeholder string   — placeholder text
 */

import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
    Table,
    TableRow,
    TableHeader,
    TableCell,
} from '@tiptap/extension-table';

import '../styles/TipTapEditor.css';

// ─── Clipboard table parsing ────────────────────────────────────────────────

const stripMarkdownCellFormatting = (value = '') => value
    .trim()
    .replace(/^\*\*(.*?)\*\*$/s, '$1')
    .replace(/^__(.*?)__$/s, '$1');

const isMarkdownSeparatorRow = (row = []) => (
    row.length > 0
    && row.every((cell) => /^:?-{3,}:?$/.test(String(cell).trim()))
);

const parseTabularClipboardText = (rawText = '') => {
    const text = String(rawText || '').replace(/\r\n?/g, '\n').trim();
    if (!text) return null;

    const lines = text.split('\n').filter((line) => line.trim().length > 0);
    if (lines.length < 2) return null;

    // Google Sheets / Excel copy: cells are separated by TABs.
    if (lines.some((line) => line.includes('\t'))) {
        const rows = lines.map((line) => line.split('\t').map(stripMarkdownCellFormatting));
        const columnCount = Math.max(...rows.map((row) => row.length));
        if (columnCount < 2) return null;
        return {
            rows: rows.map((row) => [...row, ...Array(columnCount - row.length).fill('')]),
            hasHeaderRow: true,
        };
    }

    // Markdown table copy: | Heading | Value |
    const pipeRows = lines
        .filter((line) => line.includes('|'))
        .map((line) => {
            let normalized = line.trim();
            if (normalized.startsWith('|')) normalized = normalized.slice(1);
            if (normalized.endsWith('|')) normalized = normalized.slice(0, -1);
            return normalized.split('|').map(stripMarkdownCellFormatting);
        });

    if (pipeRows.length < 2) return null;

    const separatorIndex = pipeRows.findIndex(isMarkdownSeparatorRow);
    const rows = pipeRows.filter((_, index) => index !== separatorIndex);
    const columnCount = Math.max(...rows.map((row) => row.length));
    if (rows.length < 2 || columnCount < 2) return null;

    return {
        rows: rows.map((row) => [...row, ...Array(columnCount - row.length).fill('')]),
        hasHeaderRow: separatorIndex === 1,
    };
};

const createTableNodeFromRows = (schema, rows, hasHeaderRow) => {
    const { table, tableRow, tableHeader, tableCell, paragraph } = schema.nodes;
    if (!table || !tableRow || !tableCell || !paragraph) return null;

    const tableRows = rows.map((row, rowIndex) => {
        const CellType = hasHeaderRow && rowIndex === 0 && tableHeader ? tableHeader : tableCell;
        const cells = row.map((cellValue) => {
            const text = String(cellValue ?? '');
            const paragraphContent = text ? schema.text(text) : undefined;
            return CellType.create(null, paragraph.create(null, paragraphContent));
        });
        return tableRow.create(null, cells);
    });

    return table.create(null, tableRows);
};

// ─── Toolbar button ───────────────────────────────────────────────────────────

function ToolbarButton({ onClick, active, disabled, title, children }) {
    return (
        <button
            type="button"
            title={title}
            disabled={disabled}
            className={`tte-btn${active ? ' tte-btn--active' : ''}`}
            onMouseDown={(e) => {
                e.preventDefault(); // prevent editor losing focus
                onClick?.();
            }}
        >
            {children}
        </button>
    );
}

// ─── Toolbar ─────────────────────────────────────────────────────────────────

function Toolbar({ editor, disabled }) {
    if (!editor) return null;

    const setLink = () => {
        const url = window.prompt('Enter URL');
        if (!url) {
            editor.chain().focus().unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({ href: url }).run();
    };

    return (
        <div className={`tte-toolbar${disabled ? ' tte-toolbar--disabled' : ''}`}>
            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Bold"
                    disabled={disabled}
                    active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <strong>B</strong>
                </ToolbarButton>

                <ToolbarButton
                    title="Italic"
                    disabled={disabled}
                    active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <em>I</em>
                </ToolbarButton>

                <ToolbarButton
                    title="Underline"
                    disabled={disabled}
                    active={editor.isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                >
                    <span style={{ textDecoration: 'underline' }}>U</span>
                </ToolbarButton>

                <ToolbarButton
                    title="Strikethrough"
                    disabled={disabled}
                    active={editor.isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                    <s>S</s>
                </ToolbarButton>
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Bullet List"
                    disabled={disabled}
                    active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    ≡
                </ToolbarButton>

                <ToolbarButton
                    title="Ordered List"
                    disabled={disabled}
                    active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    1.
                </ToolbarButton>
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Link"
                    disabled={disabled}
                    active={editor.isActive('link')}
                    onClick={setLink}
                >
                    🔗
                </ToolbarButton>
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group tte-table-tools">
                <ToolbarButton
                    title="Insert 3 × 3 table"
                    disabled={disabled}
                    active={editor.isActive('table')}
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                >
                    ▦
                </ToolbarButton>

                {editor.isActive('table') && (
                    <>
                        <ToolbarButton
                            title="Add column"
                            disabled={disabled || !editor.can().addColumnAfter()}
                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                        >
                            C+
                        </ToolbarButton>
                        <ToolbarButton
                            title="Delete column"
                            disabled={disabled || !editor.can().deleteColumn()}
                            onClick={() => editor.chain().focus().deleteColumn().run()}
                        >
                            C−
                        </ToolbarButton>
                        <ToolbarButton
                            title="Add row"
                            disabled={disabled || !editor.can().addRowAfter()}
                            onClick={() => editor.chain().focus().addRowAfter().run()}
                        >
                            R+
                        </ToolbarButton>
                        <ToolbarButton
                            title="Delete row"
                            disabled={disabled || !editor.can().deleteRow()}
                            onClick={() => editor.chain().focus().deleteRow().run()}
                        >
                            R−
                        </ToolbarButton>
                        <ToolbarButton
                            title="Toggle header row"
                            disabled={disabled || !editor.can().toggleHeaderRow()}
                            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                        >
                            H
                        </ToolbarButton>
                        <ToolbarButton
                            title="Delete table"
                            disabled={disabled || !editor.can().deleteTable()}
                            onClick={() => editor.chain().focus().deleteTable().run()}
                        >
                            🗑
                        </ToolbarButton>
                    </>
                )}
            </div>

            <div className="tte-toolbar-divider" />

            <div className="tte-toolbar-group">
                <ToolbarButton
                    title="Clear formatting"
                    disabled={disabled}
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                >
                    ✕
                </ToolbarButton>
            </div>
        </div>
    );
}

// ─── Main editor ──────────────────────────────────────────────────────────────

export default function TipTapEditor({
    value = '',
    onChange,
    disabled = false,
    placeholder = '',
}) {
    // Tracks the last value this editor itself produced (via onUpdate) or was
    // last told to show (via the sync effect below) — NOT the editor's live
    // getHTML(), which can drift from `value` after a round-trip through
    // TipTap's HTML serializer (e.g. plain AI-generated text with no <p> tags
    // never equals its own wrapped-in-<p> serialization, which previously made
    // the old getHTML()-based comparison useless as a "did this come from us"
    // check and caused an external update to fight with a stale echo).
    const lastKnownValueRef = useRef(value ?? '');

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { rel: 'noopener noreferrer' },
            }),
            Table.configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'email-template-table',
                },
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: value,           // set initial content correctly on mount
        editable: !disabled,
        editorProps: {
            attributes: {
                class: 'tte-content',
            },
            handlePaste: (view, event) => {
                const clipboardText = event.clipboardData?.getData('text/plain') || '';
                const parsedTable = parseTabularClipboardText(clipboardText);
                if (!parsedTable) return false;

                const tableNode = createTableNodeFromRows(
                    view.state.schema,
                    parsedTable.rows,
                    parsedTable.hasHeaderRow,
                );
                if (!tableNode) return false;

                event.preventDefault();
                const transaction = view.state.tr.replaceSelectionWith(tableNode, false).scrollIntoView();
                view.dispatch(transaction);
                return true;
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const next = html === '<p></p>' ? '' : html;
            lastKnownValueRef.current = next;
            onChange?.(next);
        },
    });

    // Sync value from outside — handles setFieldsValue from Ant Design form
    // (e.g. an AI Action replacing the content). Skips only when the incoming
    // value is exactly what this editor itself last emitted, so a genuine
    // external update always applies even if it differs from getHTML() purely
    // due to HTML serialization (missing <p> wrapper, entity encoding, etc).
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const nextValue = value || '';
        if (nextValue === (lastKnownValueRef.current || '')) return;
        lastKnownValueRef.current = nextValue;
        editor.commands.setContent(nextValue, false);
    }, [value, editor]);

    // Sync disabled state
    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [disabled, editor]);

    return (
        <div className={`tte-wrapper${disabled ? ' tte-wrapper--disabled' : ''}`}>
            <Toolbar editor={editor} disabled={disabled} />
            <EditorContent editor={editor} />
            {!value && !editor?.isFocused && placeholder && (
                <div className="tte-placeholder">{placeholder}</div>
            )}
        </div>
    );
}
